import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { AlertTriangle, X, Search, Banknote, Smartphone } from 'lucide-react';
import api from '../../api';

const PERSONAL_COLORS = [
  { hex: '#FF6B6B', name: 'Rojo' },
  { hex: '#FF9F43', name: 'Naranja' },
  { hex: '#FFD93D', name: 'Amarillo' },
  { hex: '#6BCB77', name: 'Verde' },
  { hex: '#4ECDC4', name: 'Turquesa' },
  { hex: '#4A9EFF', name: 'Azul' },
  { hex: '#9B59B6', name: 'Violeta' },
  { hex: '#FF6BB5', name: 'Rosa' },
  { hex: '#A0785A', name: 'Marrón' },
  { hex: '#95A5A6', name: 'Gris' },
];

const PERIODS = [
  { key: 'daily', label: 'Hoy' },
  { key: 'weekly', label: 'Semana' },
  { key: 'monthly', label: 'Mes' },
  { key: 'yearly', label: 'Año' },
  { key: 'custom', label: 'Rango' },
];

const fmt = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const parseUTC = (iso) => {
  if (!iso) return null;
  if (!iso.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(iso)) return new Date(iso.replace(' ', 'T') + 'Z');
  return new Date(iso);
};
const getArgDate = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });

const getPeriodRange = (period) => {
  const todayStr = getArgDate();
  const today = new Date(todayStr + 'T12:00:00');
  if (period === 'daily') return { from: todayStr, to: todayStr };
  if (period === 'weekly') {
    const d = new Date(today);
    const dow = d.getDay();
    d.setDate(d.getDate() + (dow === 0 ? -6 : 1 - dow));
    return { from: d.toLocaleDateString('en-CA'), to: todayStr };
  }
  if (period === 'monthly') {
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    return { from: `${y}-${m}-01`, to: todayStr };
  }
  if (period === 'yearly') return { from: `${today.getFullYear()}-01-01`, to: todayStr };
  return null;
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload?.length) {
    return (
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px' }}>
        <p style={{ margin: 0, fontWeight: 700 }}>{payload[0].name}</p>
        <p style={{ margin: 0, color: payload[0].payload.color }}>{fmt(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

export default function PersonalExpenses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('monthly');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [form, setForm] = useState({ description: '', amount: '', color: '', payment_method: '' });
  const [paymentAlert, setPaymentAlert] = useState(false);
  const [colorAlert, setColorAlert] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [deleteExpense, setDeleteExpense] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const range = period === 'custom'
        ? (fromDate && toDate ? { from: fromDate, to: toDate } : null)
        : getPeriodRange(period);
      const params = range ? { start_date: range.from, end_date: range.to } : {};
      const r = await api.get(`/personal-expenses/${user.id}`, { params });
      setExpenses(r.data);
    } catch (e) { console.error(e); }
  }, [user?.id, period, fromDate, toDate]);

  useEffect(() => {
    if (period === 'custom' && (!fromDate || !toDate)) return;
    load();
  }, [load]);

  const openModal = () => {
    setForm({ description: '', amount: '', color: '', payment_method: '' });
    setPaymentAlert(false);
    setColorAlert(false);
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.description || !form.amount) { alert('Completa todos los campos'); return; }
    if (!form.color) {
      setColorAlert(true);
      setTimeout(() => setColorAlert(false), 3000);
      return;
    }
    if (!form.payment_method) {
      setPaymentAlert(true);
      setTimeout(() => setPaymentAlert(false), 3000);
      return;
    }
    setLoading(true);
    try {
      await api.post('/personal-expenses', {
        business_user_id: user?.id,
        description: form.description,
        amount: parseFloat(form.amount),
        color: form.color,
        payment_method: form.payment_method,
      });
      setShowModal(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/personal-expenses/${deleteExpense.id}`);
      setDeleteExpense(null);
      load();
    } catch { alert('Error al eliminar'); }
    finally { setDeleting(false); }
  };

  // Search filter
  const filteredExpenses = searchQuery.trim()
    ? expenses.filter(e => e.description.toLowerCase().includes(searchQuery.toLowerCase()))
    : expenses;

  const searchTotal = filteredExpenses.reduce((s, e) => s + e.amount, 0);
  const totalEfectivo = filteredExpenses.filter(e => e.payment_method === 'efectivo').reduce((s, e) => s + e.amount, 0);
  const totalTransferencia = filteredExpenses.filter(e => e.payment_method === 'transferencia').reduce((s, e) => s + e.amount, 0);

  // Chart data grouped by color
  const colorGroups = PERSONAL_COLORS.map(c => ({
    name: c.name,
    value: filteredExpenses.filter(e => e.color === c.hex).reduce((s, e) => s + e.amount, 0),
    color: c.hex,
  })).filter(d => d.value > 0);

  return (
    <div className="page-full">
      <div className="header">
        <button className="icon-btn" onClick={() => navigate(-1)}>←</button>
        <h1 className="header-title">Gastos Personales</h1>
        <button
          className="btn-primary"
          data-testid="add-personal-expense-btn"
          style={{ fontSize: '13px', padding: '6px 14px' }}
          onClick={openModal}
        >
          + Registrar
        </button>
      </div>
      <div className="content-scroll">

        {/* Info banner */}
        <div style={{
          background: 'rgba(155,89,182,0.08)',
          border: '1px solid rgba(155,89,182,0.3)',
          borderRadius: 12,
          padding: '12px 14px',
          marginBottom: 16,
          fontSize: '12px',
          color: 'var(--text2)',
          lineHeight: 1.5,
          textAlign: 'center',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
        }}>
          Estos gastos personales NO SE CONTABILIZARÁN CON TUS GANANCIAS, SOLO ES PARA LLEVAR UN CONTROL DE TUS GASTOS PERSONALES SI LO DESEAS
        </div>

        {/* Period Tabs */}
        <div className="period-tabs" style={{ marginBottom: 0 }}>
          {PERIODS.map(p => (
            <button
              key={p.key}
              data-testid={`personal-period-${p.key}`}
              className={`period-tab ${period === p.key ? 'active' : ''}`}
              onClick={() => setPeriod(p.key)}
            >{p.label}</button>
          ))}
        </div>

        {period === 'custom' && (
          <div className="date-range-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="label">Desde</label>
              <input className="input" type="date" data-testid="personal-range-from" value={fromDate} onChange={e => setFromDate(e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="label">Hasta</label>
              <input className="input" type="date" data-testid="personal-range-to" value={toDate} onChange={e => setToDate(e.target.value)} />
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div style={{ padding: '12px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '8px 14px', gap: 8 }}>
            <Search size={16} color="var(--text2)" />
            <input
              data-testid="personal-expense-search"
              style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--text)', fontSize: '14px' }}
              placeholder="Buscar gasto personal..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)', padding: 0 }}>
                <X size={16} />
              </button>
            )}
          </div>
          {searchQuery.trim() && (
            <div style={{ marginTop: 8, padding: '10px 14px', background: 'rgba(155,89,182,0.08)', border: '1px solid rgba(155,89,182,0.25)', borderRadius: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: 'var(--text2)' }}>
                  {filteredExpenses.length} resultado(s) para "{searchQuery}"
                </span>
                <span style={{ fontSize: '15px', fontWeight: 800, color: '#9B59B6' }}>{fmt(searchTotal)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Payment summary */}
        {filteredExpenses.length > 0 && (
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <div style={{ flex: 1, background: 'rgba(78,205,196,0.08)', border: '1px solid rgba(78,205,196,0.25)', borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: 'var(--text2)', marginBottom: 2 }}>Efectivo</div>
              <div style={{ fontSize: '16px', fontWeight: 800, color: '#4ECDC4' }}>{fmt(totalEfectivo)}</div>
            </div>
            <div style={{ flex: 1, background: 'rgba(74,158,255,0.08)', border: '1px solid rgba(74,158,255,0.25)', borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: 'var(--text2)', marginBottom: 2 }}>Transferencia</div>
              <div style={{ fontSize: '16px', fontWeight: 800, color: '#4A9EFF' }}>{fmt(totalTransferencia)}</div>
            </div>
          </div>
        )}

        {/* Pie chart */}
        {colorGroups.length > 0 && (
          <div className="card" style={{ marginBottom: 16, marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>Distribución por Categoría</span>
              <span style={{ fontWeight: 800, color: '#9B59B6', fontSize: '1rem' }}>{fmt(filteredExpenses.reduce((s, e) => s + e.amount, 0))}</span>
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
              {colorGroups.map(d => (
                <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: d.color }} />
                  <span style={{ color: 'var(--text2)' }}>{d.name}</span>
                  <span style={{ fontWeight: 700, color: d.color }}>{fmt(d.value)}</span>
                </div>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={colorGroups} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                  {colorGroups.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Expense list */}
        <h2 className="section-title" style={{ marginTop: colorGroups.length > 0 ? 0 : 14 }}>Gastos Registrados</h2>
        {filteredExpenses.map(exp => (
          <div
            key={exp.id}
            className="card-row"
            data-testid={`personal-expense-item-${exp.id}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedExpense(exp)}
          >
            <div style={{ width: 6, alignSelf: 'stretch', borderRadius: '6px 0 0 6px', background: exp.color, marginRight: 10, flexShrink: 0 }} />
            <div className="card-row-info">
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '11px', background: `${exp.color}22`, color: exp.color, padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>
                  {PERSONAL_COLORS.find(c => c.hex === exp.color)?.name || 'Color'}
                </span>
                <span style={{ fontSize: '11px', background: exp.payment_method === 'efectivo' ? 'rgba(78,205,196,0.12)' : 'rgba(74,158,255,0.12)', color: exp.payment_method === 'efectivo' ? '#4ECDC4' : '#4A9EFF', padding: '2px 7px', borderRadius: 4, fontWeight: 600 }}>
                  {exp.payment_method === 'efectivo' ? 'Efectivo' : 'Transferencia'}
                </span>
              </div>
              <div className="card-row-title" style={{ fontSize: '15px' }}>{exp.description}</div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: exp.color }}>{fmt(exp.amount)}</div>
              <div style={{ fontSize: '11px', color: 'var(--text2)', marginTop: 4 }}>Ver detalle ›</div>
            </div>
            <button
              className="icon-btn-sm danger"
              data-testid={`delete-personal-expense-${exp.id}`}
              onClick={e => { e.stopPropagation(); setDeleteExpense(exp); }}
            >🗑️</button>
          </div>
        ))}
        {filteredExpenses.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon">👤</span>
            <p className="empty-text">{searchQuery ? 'No hay resultados para tu búsqueda' : 'No hay gastos personales en este período'}</p>
          </div>
        )}
      </div>

      {/* Register Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="modal-title" style={{ margin: 0 }}>Registrar Gasto Personal</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)' }}>
                <X size={22} />
              </button>
            </div>
            <form onSubmit={save}>
              <div className="form-group">
                <label className="label">Descripción *</label>
                <input
                  className="input"
                  data-testid="personal-expense-description"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="label">Monto *</label>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  data-testid="personal-expense-amount"
                  value={form.amount}
                  onChange={e => setForm({ ...form, amount: e.target.value })}
                  required
                />
              </div>

              {/* Color picker */}
              <div className="form-group">
                <label className="label">Color de categoría *</label>
                {colorAlert && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,82,82,0.1)', border: '1px solid #ff5252', borderRadius: 8, padding: '8px 12px', marginBottom: 8, fontSize: '12px', color: '#ff5252', fontWeight: 600 }}>
                    <AlertTriangle size={14} /> Seleccioná un color
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {PERSONAL_COLORS.map(c => (
                    <button
                      key={c.hex}
                      type="button"
                      data-testid={`color-btn-${c.name.toLowerCase()}`}
                      onClick={() => setForm({ ...form, color: c.hex })}
                      title={c.name}
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        background: c.hex,
                        border: form.color === c.hex ? '3px solid var(--text)' : '2px solid transparent',
                        cursor: 'pointer',
                        boxShadow: form.color === c.hex ? `0 0 0 2px var(--bg), 0 0 0 4px ${c.hex}` : 'none',
                        transition: 'box-shadow 0.15s',
                      }}
                    />
                  ))}
                </div>
                {form.color && (
                  <div style={{ marginTop: 6, fontSize: '12px', color: 'var(--text2)' }}>
                    Color seleccionado: <span style={{ color: form.color, fontWeight: 700 }}>{PERSONAL_COLORS.find(c => c.hex === form.color)?.name}</span>
                  </div>
                )}
              </div>

              {/* Payment method */}
              <div className="form-group">
                <label className="label">Forma de pago *</label>
                {paymentAlert && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,82,82,0.1)', border: '1px solid #ff5252', borderRadius: 8, padding: '8px 12px', marginBottom: 8, fontSize: '12px', color: '#ff5252', fontWeight: 600 }}>
                    <AlertTriangle size={14} /> Seleccioná una forma de pago
                  </div>
                )}
                <div className="payment-row">
                  <button
                    type="button"
                    data-testid="personal-payment-efectivo"
                    className={`payment-btn ${form.payment_method === 'efectivo' ? 'active' : ''}`}
                    onClick={() => setForm({ ...form, payment_method: 'efectivo' })}
                  >
                    <Banknote size={16} /> Efectivo
                  </button>
                  <button
                    type="button"
                    data-testid="personal-payment-transferencia"
                    className={`payment-btn ${form.payment_method === 'transferencia' ? 'active' : ''}`}
                    onClick={() => setForm({ ...form, payment_method: 'transferencia' })}
                  >
                    <Smartphone size={16} /> Transferencia
                  </button>
                </div>
              </div>

              <div className="modal-buttons">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" data-testid="save-personal-expense-btn" disabled={loading}>
                  {loading ? 'Guardando...' : 'Registrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selectedExpense && (
        <div className="modal-overlay" onClick={() => setSelectedExpense(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="modal-title" style={{ margin: 0 }}>Detalle de Gasto Personal</h3>
              <button onClick={() => setSelectedExpense(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)' }}>
                <X size={22} />
              </button>
            </div>

            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: selectedExpense.color, margin: '0 auto 8px' }} />
              <span style={{ fontSize: '13px', color: 'var(--text2)' }}>
                {PERSONAL_COLORS.find(c => c.hex === selectedExpense.color)?.name}
              </span>
            </div>

            <div style={{ textAlign: 'center', marginBottom: 20, padding: '14px', background: 'var(--surface)', borderRadius: 12 }}>
              <div style={{ fontSize: '11px', color: 'var(--text2)', marginBottom: 4 }}>Monto</div>
              <div style={{ fontSize: '34px', fontWeight: 800, color: selectedExpense.color }}>{fmt(selectedExpense.amount)}</div>
            </div>

            {[
              { label: 'Descripción', value: selectedExpense.description },
              {
                label: 'Forma de pago',
                value: selectedExpense.payment_method === 'efectivo' ? 'Efectivo' : 'Transferencia',
                valueStyle: { color: selectedExpense.payment_method === 'efectivo' ? '#4ECDC4' : '#4A9EFF', fontWeight: 700 },
              },
              {
                label: 'Fecha de registro',
                value: selectedExpense.created_at
                  ? parseUTC(selectedExpense.created_at)?.toLocaleString('es-AR', {
                      timeZone: 'America/Argentina/Buenos_Aires',
                      day: '2-digit', month: '2-digit', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', hour12: false,
                    })
                  : '-',
              },
            ].map(({ label, value, valueStyle }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: '13px', color: 'var(--text2)', flexShrink: 0, marginRight: 12 }}>{label}</span>
                <span style={{ fontSize: '14px', fontWeight: 600, textAlign: 'right', ...valueStyle }}>{value}</span>
              </div>
            ))}

            <button className="btn-secondary" style={{ width: '100%', marginTop: 20 }} onClick={() => setSelectedExpense(null)}>
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteExpense && (
        <div className="modal-overlay" onClick={() => setDeleteExpense(null)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Eliminar Gasto Personal</h3>
            <p style={{ textAlign: 'center', color: 'var(--text2)', marginBottom: 20 }}>
              ¿Eliminar <strong style={{ color: 'var(--text)' }}>{deleteExpense.description}</strong>?
              <br />Esta acción no se puede deshacer.
            </p>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setDeleteExpense(null)}>Cancelar</button>
              <button
                data-testid="confirm-delete-personal-expense-btn"
                className="btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
                style={{ flex: 1 }}
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
